import { useState, useEffect } from 'react';

/**
 * useOnlineStatus Hook
 * 
 * Detects and tracks online/offline status in real-time.
 * Listens to browser's online/offline events for immediate updates.
 * 
 * Features:
 * - Real-time status updates
 * - Automatic event listener cleanup
 * - Cross-browser compatible
 * - Lightweight (no dependencies)
 * 
 * Industry Pattern: React Query's online detection, SWR's network status
 * 
 * @hook
 * @example
 * ```tsx
 * function App() {
 *   const isOnline = useOnlineStatus();
 *   
 *   if (!isOnline) {
 *     return <OfflinePage />;
 *   }
 *   
 *   return <MainApp />;
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Show toast when connection status changes
 * function Dashboard() {
 *   const isOnline = useOnlineStatus();
 *   const { showToast } = useToast();
 *   
 *   useEffect(() => {
 *     if (isOnline) {
 *       showToast('Connection restored!', 'success');
 *     } else {
 *       showToast('Lost connection', 'error');
 *     }
 *   }, [isOnline, showToast]);
 *   
 *   return <div>...</div>;
 * }
 * ```
 * 
 * @returns {boolean} true if online, false if offline
 */
export function useOnlineStatus(): boolean {
  // Initialize with current online status
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    // navigator.onLine is available in all modern browsers
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  });

  useEffect(() => {
    // Event handlers
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

